import { useEffect, useMemo, useState } from 'react';
import { Comment } from '@mui/icons-material';
import { Box, CircularProgress, Divider, IconButton, ListItem, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ListCell, ListDataColumn, ListHeader, ViewNotesModal } from '/src/components';
import { useLoanStore } from '/src/context';
import { Loan, Note } from '/src/lib/models';
import { TListLoansRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type LoansListProps = {
  /**
   * List of Loans to display
   */
  data?: Loan[];
  /**
   * Query to include when fetching Loans
   */
  query?: TListLoansRequest;
};

/**
 * Loans List
 */
export const LoansList: React.FC<LoansListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const loanStore = useLoanStore();

  /** State **/
  const [loans, setLoans] = useState<Loan[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (loans) {
      const list = loans.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [loans, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Loan[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.principalBalance = 0;
    totals.feeBalance = 0;
    totals.interestBalance = 0;
    totals.assetValue = 0;

    const loans = data.map((loan) => {
      // Combine
      loan.notes = [...loan.socketComments, ...loan.companyComments].sort((a, b) => (a.created > b.created ? 1 : -1));

      // Calculate totals
      totals.principalBalance += loan.principalBalance;
      totals.feeBalance += loan.feeBalance;
      totals.interestBalance += loan.interestBalance;
      totals.assetValue += loan.assetValue;

      // Store
      return loan;
    });

    // Save data
    setLoans(loans);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      loanStore.query(query).then((loans) => loans && formatData(loans));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { flex: 5, propName: 'notes', label: 'Notes' },
            { propName: 'name', label: 'Name' },
            { propName: 'principalBalance', label: 'Principal Balance' },
            { propName: 'feeBalance', label: 'Fee Balance' },
            { propName: 'interestBalance', label: 'Interest Balance' },
            { propName: 'assetValue', label: 'Current Asset Value' },
            { propName: 'interestRate', label: 'Interest Rate' },
            { propName: 'status', label: 'Status' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((loan, i) => (
            <ListItem
              key={loan._id}
              className={`capitalize ${i % 2 ? '' : 'highlighted'}`}
              sx={{ height: rowHeight + 'px', maxHeight: rowHeight + 'px' }}
            >
              <Box
                display="flex"
                flex="100"
                justifyContent="space-around"
                alignItems="center"
                className="text-center md-subhead-sm"
              >
                {/* NOTES */}
                <Box component="span" display="flex" flex={5} maxWidth="5%">
                  {(!!loan.companyComments?.length || !!loan.socketComments?.length) && (
                    <IconButton onClick={() => showNotesPopup(loan.notes, loan.name)} className="pointer">
                      <Tooltip title="Click to see Notes">
                        <Comment />
                      </Tooltip>
                    </IconButton>
                  )}
                </Box>

                {/* LOAN NAME */}
                <ListDataColumn label="Deal Name" value={loan.name} url={`/deals/${loan._id}`} />

                {/* PRINCIPAL BALANCE */}
                <ListDataColumn
                  label="Principal Balance"
                  value={utils.currency.format(loan.principalBalance)}
                  textAlign="right"
                />

                {/* FEE BALANCE */}
                <ListDataColumn label="Fee Balance" value={utils.currency.format(loan.feeBalance)} textAlign="right" />

                {/* INTEREST BALANCE */}
                <ListDataColumn
                  label="Interest Balance"
                  value={utils.currency.format(loan.interestBalance)}
                  textAlign="right"
                />

                {/* CURRENT ASSET VALUE */}
                <ListDataColumn
                  label="Current Asset Value"
                  value={utils.currency.format(loan.assetValue)}
                  textAlign="right"
                />

                {/* INTEREST RATE */}
                <ListDataColumn
                  label="Interest Rate"
                  value={utils.percent.format(loan.interestRate)}
                  textAlign="right"
                />

                {/* STATUS */}
                <ListDataColumn label="Status" value={loan.status} textAlign="right" />
              </Box>
            </ListItem>
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { flex: 5, propName: 'notes', label: 'Notes' },
              { propName: 'name', label: 'Name' },
              { propName: 'principalBalance', label: 'Principal Balance' },
              { propName: 'feeBalance', label: 'Fee Balance' },
              { propName: 'interestBalance', label: 'Interest Balance' },
              { propName: 'assetValue', label: 'Current Asset Value' },
              { propName: 'interestRate', label: 'Interest Rate', type: 'percent' },
              { propName: 'status', label: 'Status' },
            ].map(({ propName, type, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={
                  i === 0
                    ? 'Totals'
                    : utils.isNumber(totals[propName])
                    ? utils[type === 'percent' ? 'percent' : 'currency'].format(totals[propName])
                    : ''
                }
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Deals found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
